// src/components/common/form/ElDefectsForm.tsx
import {
  addCellyield,
  deleteCellyield,
  createCellyieldPayload,
} from "../../../services/cellyieldService";
import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  DefectType,
  initialDefects,
  stringImages,
  StringSize,
} from "../../../types/stringerTypes";
import { DefectButtons } from "../../ui/buttons/DefectButtons";

interface ElDefectsFormProps {
  showDetailsOnly?: boolean;
  renderCounter?: (
    defect: DefectType,
    count: number,
    onChange: (delta: number) => void,
    disabled: boolean
  ) => React.ReactNode;
  initialCounts?: Record<StringSize, Record<DefectType, number>>;
}

const STRING_SIZES: StringSize[] = [4, 5, 6, 8];

const createInitialCellDefects = (): Record<DefectType, number> =>
  initialDefects.reduce((acc, defect) => {
    acc[defect] = 0;
    return acc;
  }, {} as Record<DefectType, number>);

export default function ElDefectsForm({
  showDetailsOnly = true,
  renderCounter,
  initialCounts,
}: ElDefectsFormProps) {
  const [stringSize, setStringSize] = useState<StringSize>(4);
  const [defectCounts, setDefectCounts] = useState<
    Record<StringSize, Record<DefectType, number>>
  >(
    initialCounts || {
      4: createInitialCellDefects(),
      5: createInitialCellDefects(),
      6: createInitialCellDefects(),
      8: createInitialCellDefects(),
    }
  );

  const [cellDefects, setCellDefects] = useState<Record<DefectType, number>>(
    createInitialCellDefects()
  );
  const [cooldown, setCooldown] = useState(false);
  const handleCountChange = async (defect: DefectType, delta: number) => {
    setCooldown(true);
    setTimeout(() => setCooldown(false), 300);
    setDefectCounts((prev) => {
      const current = prev[stringSize][defect];
      return {
        ...prev,
        [stringSize]: {
          ...prev[stringSize],
          [defect]: Math.max(0, current + delta),
        },
      };
    });

    try {
      const payload = createCellyieldPayload(defect, stringSize);

      if (delta > 0) {
        await addCellyield(payload);
      } else if (delta < 0) {
        await deleteCellyield(payload);
      }
    } catch (err) {
      console.error("❌ Fehler beim Senden:", err);
    }
  };

  const totalStrings = useMemo(() => {
    return Object.values(defectCounts).reduce(
      (sum, sizeDefects) =>
        sum + Object.values(sizeDefects).reduce((a, b) => a + b, 0),
      0
    );
  }, [defectCounts]);

  const totalCells = useMemo(() => {
    return Object.entries(defectCounts).reduce(
      (total, [sizeKey, defects]) =>
        total +
        Object.values(defects).reduce(
          (count, numStrings) => count + numStrings * Number(sizeKey),
          0
        ),
      0
    );
  }, [defectCounts]);

  const renderDefectTable = () => (
    <Table sx={{ maxWidth: "50%", justifySelf: "center" }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ border: 1 }}></TableCell>
          {initialDefects.map((defect) => (
            <TableCell
              key={defect}
              align="center"
              sx={{
                fontWeight: "bold",
                fontSize: 18,
                border: "1px solid black",
              }}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                {defect}
                <DefectButtons
                  onChange={(delta) => handleCountChange(defect, delta)}
                  disabled={cooldown}
                />
              </Box>
            </TableCell>
          ))}

          <TableCell
            data-testid="header-strings"
            sx={{
              fontWeight: "bold",
              borderTop: 1,
              borderBottom: 1,
              fontSize: 18,
            }}
          >
            # Strings
          </TableCell>
          <TableCell
            data-testid="header-cells"
            sx={{
              fontWeight: "bold",
              fontSize: 18,
              borderRight: 1,
              borderBottom: 1,
              borderTop: 1,
            }}
          >
            # Zellen
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        <TableRow>
          <TableCell data-testid="string-size-label" sx={{ border: 1 }}>
            <Box
              width={100}
              height={60}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <img
                src={stringImages[stringSize]}
                alt={`${stringSize}er String`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
            <br />
            {stringSize}er String
          </TableCell>

          {initialDefects.map((defect) => (
            <TableCell
              key={defect}
              align="center"
              sx={{ border: 1, fontWeight: "bold", fontSize: 18 }}
              data-testid={`${defect.toLowerCase()}-counter`}
            >
              {defectCounts[stringSize][defect]}
            </TableCell>
          ))}

          <TableCell
            data-testid="total-strings"
            sx={{ fontWeight: "bold", fontSize: 18, borderBottom: 1 }}
          >
            {totalStrings}
          </TableCell>
          <TableCell
            data-testid="total-cells"
            sx={{
              fontWeight: "bold",
              fontSize: 18,
              borderRight: 1,
              borderBottom: 1,
            }}
          >
            {totalCells}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  const renderStringSizeButtons = () => (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        mt: 8,
        color: "#b91c1c",
        justifySelf: "center",
      }}
    >
      {STRING_SIZES.map((size) => (
        <Button
          key={size}
          variant={stringSize === size ? "contained" : "outlined"}
          color="error"
          onClick={() => setStringSize(size)}
          sx={{ fontWeight: "bold" }}
        >
          Wechsle zu {size}er String
        </Button>
      ))}
    </Box>
  );

  const renderDetailsTable = () => {
    const types: DefectType[] = ["Zellbruch", "Verschmutzung"];

    const handleCellDefectChange = (defect: DefectType, delta: number) => {
      setCellDefects((prev) => ({
        ...prev,
        [defect]: Math.max(0, prev[defect] + delta),
      }));
    };

    const totalCells = types.reduce((sum, d) => sum + cellDefects[d], 0);

    return (
      <Box mt={4}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Zelle</TableCell>
              <TableCell>Zellbruch</TableCell>
              <TableCell>Verschmutzung</TableCell>
              <TableCell># Zellen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Zelle</TableCell>
              {types.map((defect) => (
                <TableCell key={defect}>
                  <Button
                    onClick={() => handleCellDefectChange(defect, -1)}
                    variant="outlined"
                    sx={{
                      m: 1,
                      minWidth: 50,
                      borderRadius: "10px",
                      color: "#b91c1c",
                      borderColor: "#b91c1c",
                    }}
                  >
                    -
                  </Button>
                  {cellDefects[defect]}
                  <Button
                    onClick={() => handleCellDefectChange(defect, 1)}
                    variant="outlined"
                    sx={{
                      m: 1,
                      minWidth: 50,
                      borderRadius: "10px",
                      color: "#b91c1c",
                      borderColor: "#b91c1c",
                    }}
                  >
                    +
                  </Button>
                </TableCell>
              ))}
              <TableCell>{totalCells}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Box>
      {renderDefectTable()}
      {renderStringSizeButtons()}
      {showDetailsOnly && renderDetailsTable()}
    </Box>
  );
}
